import { z } from 'zod';
import {
  segments,
  labels,
  normalizeSegmentConfig,
} from '@/src/io/import/configSegments';
import { configIo, RENAMED_IO_KEYS } from '@/src/io/import/configIo';
import {
  getEntries,
  isRecord,
  partition,
  plural,
  zodEnumFromObjKeys,
} from '@/src/utils';
import { ACTIONS } from '@/src/constants';
import type { Action, Binding } from '@/src/constants';

import { surfaceWarning, useMessageStore } from '@/src/store/messages';
import { useSegmentStore } from '@/src/segmentation/segments';
import {
  parseStatedColor,
  rejectedColorsMessage,
} from '@/src/segmentation/color';
import { useViewStore } from '@/src/store/views';
import { useWindowingStore } from '@/src/store/view-configs/windowing';
import {
  actionToKey,
  bindingsOf,
  isDispatchable,
} from '@/src/composables/useKeyboardShortcuts';
import { useSegmentationStore } from '@/src/segmentation/store';
import useLoadDataStore from '@/src/store/load-data';
import { layoutConfig } from '@/src/utils/layoutParsing';

// --------------------------------------------------------------------------
// Layout

const layouts = z.record(z.string(), layoutConfig).optional();

// --------------------------------------------------------------------------
// Keyboard shortcuts

const bindingSchema = z.union([z.string(), z.array(z.string())]);

const shortcuts = z
  .partialRecord(zodEnumFromObjKeys(ACTIONS), bindingSchema)
  .optional();

// --------------------------------------------------------------------------
// IO

const io = configIo.optional();

// --------------------------------------------------------------------------
// Window Level

const windowing = z
  .object({
    level: z.number(),
    width: z.number(),
  })
  .optional();

const disabledViewTypes = z
  .array(z.enum(['2D', '3D', 'Oblique', 'EnFace']))
  .optional();

const configInput = z.object({
  layouts,
  segments,
  labels,
  shortcuts,
  io,
  windowing,
  disabledViewTypes,
});

export const config = configInput.transform(normalizeSegmentConfig);

export type Config = z.infer<typeof config>;

// ---------------------------------------------------------------------------
// Config-by-shape recognition
//
// Config can arrive via any channel (launch manifest, dropped file, or a
// `urls=` entry) — it's recognized purely by shape. Trust for the
// `processing` section attaches later, to the provider's origin (see
// io/originGate), not to how the config arrived.
//
// Unknown top-level keys are tolerated (stripped, reported via
// `ignoredKeys`) so a newer config still degrades gracefully on an older
// client. Known section values are always strictly validated — the trust
// boundary is on values, not top-level keys:
//   - no known keys        => data (plain import)
//   - known + unknown keys => config; ignoredKeys lists the stripped keys
//   - only known keys      => config; ignoredKeys empty

export type ConfigRecognition =
  // `ignoredKeys` lists the unknown top-level keys that were stripped (empty
  // when every top-level key was a known section). `deprecations` holds one
  // user-facing note per legacy key that was converted or ignored.
  | {
      kind: 'config';
      config: Config;
      ignoredKeys: string[];
      deprecations: string[];
    }
  | { kind: 'data' };

// ---------------------------------------------------------------------------
// Config-section registry
//
// Feature modules contribute their own top-level config section (schema +
// apply) instead of this file naming each feature. A feature registers from
// its public entry point at module-evaluation time, which runs before any
// config file can be recognized (recognition is triggered by data import,
// after boot).

export type ConfigSection<S extends z.ZodType = z.ZodType> = {
  key: string;
  schema: S;
  apply: (value: z.output<S>) => void | Promise<void>;
};

// Keyed by section key so re-registration (e.g. a test's fresh module graph)
// stays idempotent.
const configSections = new Map<string, ConfigSection>();

export const registerConfigSection = <S extends z.ZodType>(
  section: ConfigSection<S>
) => {
  configSections.set(section.key, section as unknown as ConfigSection);
};

// Base sections + every registered section define the known top-level keys.
const fullConfigSchema = () =>
  configInput.extend(
    Object.fromEntries(
      [...configSections.values()].map((section) => [
        section.key,
        section.schema,
      ])
    )
  );

const CONFIG_KEY_REPLACEMENTS = {
  ...Object.fromEntries(
    RENAMED_IO_KEYS.map(([legacy, key]) => [`io.${legacy}`, `io.${key}`])
  ),
  labels: 'segments',
};

const valueAt = (raw: Record<string, unknown>, path: string) =>
  path
    .split('.')
    .reduce<unknown>(
      (node, part) => (isRecord(node) ? node[part] : undefined),
      raw
    );

const hasRectangleFill = (raw: Record<string, unknown>) => {
  const rectangleLabels = valueAt(raw, 'labels.rectangleLabels');
  return (
    isRecord(rectangleLabels) &&
    Object.values(rectangleLabels).some(
      (label) => isRecord(label) && label.fillColor !== undefined
    )
  );
};

// A legacy key beside its replacement is ignored rather than converted.
const deprecationNotes = (raw: Record<string, unknown>) => [
  ...Object.entries(CONFIG_KEY_REPLACEMENTS)
    .filter(([key]) => valueAt(raw, key) !== undefined)
    .map(([key, replacement]) =>
      valueAt(raw, replacement) === undefined
        ? `${key} was migrated to ${replacement}. Update your configuration to use ${replacement}.`
        : `${key} is ignored because ${replacement} is present. Remove ${key}.`
    ),
  ...(hasRectangleFill(raw)
    ? ['Rectangle label fillColor is no longer supported and was ignored.']
    : []),
];

export const recognizeConfig = async (
  raw: unknown
): Promise<ConfigRecognition> => {
  if (!isRecord(raw)) return { kind: 'data' };

  const fullConfig = fullConfigSchema();
  const knownKeys = new Set(Object.keys(fullConfig.shape));

  const presentKeys = Object.keys(raw);
  const knownPresent = presentKeys.filter((key) => knownKeys.has(key));
  // No config signal at all — a plain data JSON. Falls through silently so a
  // stray data file is not announced as config.
  if (knownPresent.length === 0) return { kind: 'data' };

  // `fullConfig.parse` relies on zod's default (non-strict) object behavior to
  // drop unknown keys; adding `.strict()` would silently break forward-compat.
  const ignoredKeys = presentKeys.filter((key) => !knownKeys.has(key));
  return {
    kind: 'config',
    config: fullConfig.transform(normalizeSegmentConfig).parse(raw),
    ignoredKeys,
    deprecations: deprecationNotes(raw),
  };
};

export const recognizeConfigFile = async (
  file: File
): Promise<ConfigRecognition> => {
  return recognizeConfig(JSON.parse(await file.text()));
};

// The registry silently drops an unparseable color, so report it here.
const reportUnparseableColors = (
  configured: NonNullable<Config['segments']>,
  addError: (message: string) => void
) => {
  const rejected = new Set<string>();
  Object.entries(configured).forEach(([name, { color }]) => {
    parseStatedColor(name, color, rejected);
  });
  const message = rejectedColorsMessage('config', rejected);
  if (message) addError(message);
};

// An omitted section leaves the registry alone; an empty record or null
// clears what an earlier config contributed to it.
const applySegments = (
  manifest: Config,
  addError: (message: string) => void
) => {
  const configured = manifest.segments;
  if (configured === undefined) return;
  if (configured) reportUnparseableColors(configured, addError);
  useSegmentStore().segments.replaceConfigSegments(configured);
};

const applyLayout = (manifest: Config) => {
  if (!manifest.layouts) return;

  const viewStore = useViewStore();
  const layoutEntries = Object.entries(manifest.layouts);

  if (layoutEntries.length === 0) return;

  viewStore.setNamedLayoutsFromConfig(manifest.layouts);

  const firstLayoutName = layoutEntries[0][0];
  viewStore.switchToNamedLayout(firstLayoutName);
};

/**
 * Drops the bindings that could never fire, keeping the rest of the config.
 * A shortcut that does nothing is worth a warning, not a rejected file.
 */
const dispatchableShortcuts = (
  configured: NonNullable<Config['shortcuts']>
) => {
  const perAction = getEntries(configured).flatMap(([action, binding]) =>
    binding
      ? [[action, partition(isDispatchable, bindingsOf(binding))] as const]
      : []
  );

  const undispatchable = perAction.flatMap(([, [, rejected]]) => rejected);
  if (undispatchable.length) {
    const noun = plural(undispatchable.length, 'shortcut');
    surfaceWarning(
      'Unbindable shortcut',
      `Ignored unbindable ${noun}: ${undispatchable.join(', ')}. ` +
        'The characters + - and _ separate the keys of a chord.'
    );
  }

  return Object.fromEntries(
    perAction
      .filter(([, [usable]]) => usable.length)
      .map(([action, [usable]]) => [action, usable])
  ) as Partial<Record<Action, Binding>>;
};

const applyShortcuts = (manifest: Config) => {
  if (!manifest.shortcuts) return;

  actionToKey.value = {
    ...actionToKey.value,
    ...dispatchableShortcuts(manifest.shortcuts),
  };
};

const applyIo = (manifest: Config) => {
  if (!manifest.io) return;

  if (manifest.io.segmentationSaveFormat)
    useSegmentationStore().saveFormat = manifest.io.segmentationSaveFormat;
  const loadDataStore = useLoadDataStore();
  loadDataStore.segmentationExtension = manifest.io.segmentationExtension;
  loadDataStore.layerExtension = manifest.io.layerExtension;
};

const applyWindowing = (manifest: Config) => {
  if (!manifest.windowing) return;

  useWindowingStore().runtimeConfigWindowLevel = manifest.windowing;
};

const applyDisabledViewTypes = (manifest: Config) => {
  if (!manifest.disabledViewTypes) return;

  useViewStore().disabledViewTypes = manifest.disabledViewTypes;
};

// Registered sections are independent of one another, so apply concurrently.
const applyRegisteredSections = (manifest: Config) =>
  Promise.all(
    [...configSections.values()].map((section) =>
      section.apply((manifest as Record<string, unknown>)[section.key])
    )
  );

export const applyPreStateConfig = async (manifest: Config) => {
  applyDisabledViewTypes(manifest);
  applyLayout(manifest);
  applyShortcuts(manifest);
  applyIo(manifest);
  applyWindowing(manifest);
  await applyRegisteredSections(manifest);
};

export const applyPostStateConfig = (
  manifest: Config,
  addError: (message: string) => void = (message) =>
    useMessageStore().addError(message)
) => {
  applySegments(manifest, addError);
};
